// Content for the Médailles Animaux "L'intelligence ATNA" section.
// All dashboard figures are illustrative demo data, not client results.
export type Bi = { fr: string; en: string }

export const INTELLIGENCE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/6030d1b7-32de-4a27-afbe-c22c68bc05e7.jpg'

export const intelligenceCopy = {
  eyebrow: { fr: 'L’intelligence ATNA', en: 'ATNA intelligence' },
  line1: { fr: 'Votre registre', en: 'Your registry' },
  line2: { fr: 'devient intelligent.', en: 'becomes intelligent.' },
  body: {
    fr: 'ATNA transforme les données de votre registre en actions : elle détecte les risques, les échéances et les anomalies, identifie les priorités et recommande les prochaines actions.',
    en: 'ATNA turns your registry data into action: it detects risks, deadlines and anomalies, identifies priorities and recommends next steps.',
  },
}

export const dashCopy = {
  search: { fr: 'Rechercher un animal, un citoyen, une adresse…', en: 'Search for a pet, a resident, an address…' },
  territory: { fr: 'Tous les territoires', en: 'All territories' },
  title: { fr: 'Aperçu intelligent', en: 'Smart overview' },
  live: { fr: 'Données mises à jour en temps réel', en: 'Data updated in real time' },
  period: { fr: 'Cette semaine', en: 'This week' },
}

export const dashNav: { key: 'dashboard' | 'animals' | 'licences' | 'payments' | 'reminders' | 'reports' | 'analytics'; label: Bi }[] = [
  { key: 'dashboard', label: { fr: 'Tableau de bord', en: 'Dashboard' } },
  { key: 'animals', label: { fr: 'Animaux', en: 'Animals' } },
  { key: 'licences', label: { fr: 'Licences', en: 'Licences' } },
  { key: 'payments', label: { fr: 'Paiements', en: 'Payments' } },
  { key: 'reminders', label: { fr: 'Rappels', en: 'Reminders' } },
  { key: 'reports', label: { fr: 'Signalements', en: 'Incident reports' } },
  { key: 'analytics', label: { fr: 'Rapports', en: 'Reports' } },
]

export type Tone = 'urgent' | 'warning' | 'indigo'

export const priorityCards: { key: 'expiring' | 'incomplete' | 'expired' | 'sector'; tone: Tone; value: Bi; label: Bi; cta: Bi }[] = [
  { key: 'expiring', tone: 'urgent', value: { fr: '127', en: '127' }, label: { fr: 'licences expirent dans 30 jours', en: 'licences expire within 30 days' }, cta: { fr: 'Lancer la campagne', en: 'Launch campaign' } },
  { key: 'incomplete', tone: 'warning', value: { fr: '43', en: '43' }, label: { fr: 'dossiers incomplets', en: 'incomplete files' }, cta: { fr: 'Demander les renseignements', en: 'Request info' } },
  { key: 'expired', tone: 'urgent', value: { fr: '18', en: '18' }, label: { fr: 'licences sont déjà expirées', en: 'licences already expired' }, cta: { fr: 'Relancer', en: 'Follow up' } },
  { key: 'sector', tone: 'indigo', value: { fr: 'Secteur Nord', en: 'North sector' }, label: { fr: 'conformité sous la moyenne', en: 'compliance below average' }, cta: { fr: 'Analyser le secteur', en: 'Analyze the sector' } },
]

export const compliance = {
  title: { fr: 'Taux de conformité', en: 'Compliance rate' },
  value: { fr: '78 %', en: '78%' },
  trend: { fr: '+6 %', en: '+6%' },
  vs: { fr: 'par rapport à la période précédente', en: 'vs previous period' },
  months: { fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin'], en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] },
  points: [18, 30, 40, 50, 58, 70],
}

export const statusSplit = {
  title: { fr: 'Répartition des statuts', en: 'Status breakdown' },
  total: { fr: '12 482', en: '12,482' },
  unit: { fr: 'animaux', en: 'animals' },
  items: [
    { key: 'active', pct: 82, color: '#22A04F', label: { fr: 'Licences actives', en: 'Active' } },
    { key: 'renew', pct: 12, color: '#F2B233', label: { fr: 'À renouveler', en: 'To renew' } },
    { key: 'expired', pct: 6, color: '#E5484D', label: { fr: 'Licences expirées', en: 'Expired' } },
  ],
}

export const revenue = {
  title: { fr: 'Revenus potentiels à récupérer', en: 'Potential revenue to recover' },
  value: { fr: '8 460 $', en: '$8,460' },
  body: { fr: 'Licences expirées ou à renouveler dans 30 jours', en: 'Licences expired or due for renewal within 30 days' },
  cta: { fr: 'Voir les licences concernées', en: 'View affected licences' },
}

export const recommendation = {
  header1: { fr: 'ATNA recommande', en: 'ATNA recommends' },
  header2: { fr: ' aujourd’hui', en: ' today' },
  priority: { fr: 'Priorité nº 1 — Renouvellements', en: 'Priority #1 — Renewals' },
  body: {
    fr: '127 licences arrivent à échéance dans les 30 prochains jours. Une campagne ciblée maintenant pourrait réduire les expirations et améliorer le taux de renouvellement.',
    en: '127 licences expire in the next 30 days. A targeted campaign now could reduce expirations and improve the renewal rate.',
  },
  cta: { fr: 'Préparer la campagne', en: 'Prepare the campaign' },
}

export const intelligenceSteps: { key: 'understand' | 'verify' | 'detect' | 'recommend' | 'act'; title: Bi; body: Bi }[] = [
  { key: 'understand', title: { fr: 'Comprend', en: 'Understands' }, body: { fr: 'Le registre et le contexte.', en: 'The registry and its context.' } },
  { key: 'verify', title: { fr: 'Vérifie', en: 'Verifies' }, body: { fr: 'Les données et les règles applicables.', en: 'The data and applicable rules.' } },
  { key: 'detect', title: { fr: 'Détecte', en: 'Detects' }, body: { fr: 'Les risques, les échéances et les anomalies.', en: 'Risks, deadlines and anomalies.' } },
  { key: 'recommend', title: { fr: 'Recommande', en: 'Recommends' }, body: { fr: 'Les priorités et la prochaine action.', en: 'Priorities and the next action.' } },
  { key: 'act', title: { fr: 'Agit', en: 'Acts' }, body: { fr: 'Au moyen de campagnes, de rappels et de flux de travail intégrés.', en: 'Through built-in campaigns, reminders and workflows.' } },
]
